import React, { FC, ReactNode } from 'react';
import { Stack } from '@mui/material';

type Props = {
  title: ReactNode;
  action?: ReactNode;
  toolbar?: ReactNode;
  children: ReactNode;
};

/**
 * The frame every filter panel shares: a 48px header pinned above a list
 * that scrolls on its own, which is AC4.5, with an optional toolbar strip
 * pinned between the two — the CIS-18 reference panel's pill row, or the
 * search field that takes its place — so it stays put while the list
 * scrolls.
 *
 * The ceiling belongs to the whole panel, not to the list: header, toolbar
 * and list together never stand taller than the viewport allows, and the list
 * takes whatever height the pinned rows leave. A toolbar therefore shortens
 * the list instead of pushing the panel's bottom past the screen edge. The
 * ceiling is taken from the viewport rather than fixed, because the nine root
 * rows stand taller than any fixed cap that still fits a laptop screen, and
 * AC4.5 asks the list to scroll only once it exceeds the height actually
 * available. The 192px subtracted covers the chrome above the trigger and a
 * margin below the panel; the 288px floor keeps the header and a few rows
 * reachable on a very short window. A panel without a toolbar gets the same
 * list height either way. The header border is `grey.500`, the Figma
 * `#ECECEB`.
 */
const FilterPanelLayout: FC<Props> = ({ title, action, toolbar, children }) => (
  <Stack sx={{ maxHeight: 'max(288px, calc(100vh - 192px))' }}>
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap="8px"
      sx={{
        height: 48,
        px: '12px',
        borderBottom: 1,
        borderColor: 'grey.500',
        flexShrink: 0,
      }}
    >
      {title}
      {action}
    </Stack>

    {toolbar}

    <Stack sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
      {children}
    </Stack>
  </Stack>
);

export default FilterPanelLayout;
